import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ProductSheet } from "modules/product/components/product-sheet";

interface ProductSheetDialogProps {
  /** Product slug used to fetch the product detail in the sheet. */
  productID: string;
  /** Accessible label for the dialog, usually the product name. */
  ariaLabel?: string;
  /** The element that opens the dialog on click. */
  children: ReactNode;
}

/**
 * Opens the product detail sheet in a dialog when the wrapped trigger is clicked.
 * Used to inspect a product from the cart without leaving the current view.
 * @param props - The product slug, an optional accessible label and the trigger element.
 * @returns A dialog that renders `ProductSheet` for the given product.
 */
export const ProductSheetDialog = ({
  productID,
  ariaLabel,
  children,
}: ProductSheetDialogProps) => (
  <Dialog>
    <DialogTrigger asChild>{children}</DialogTrigger>
    <DialogContent className="min-w-[70%] max-h-[90vh] overflow-y-auto">
      <DialogTitle className="sr-only">
        {ariaLabel ?? "Product details"}
      </DialogTitle>
      <DialogDescription className="sr-only">
        Details, prices and similar products.
      </DialogDescription>
      <ProductSheet productID={productID} />
    </DialogContent>
  </Dialog>
);
